import { useRef } from 'react'
import styled, { css } from 'styled-components/macro'
import { darken, transparentize } from 'polished'

import { StyledButtonProps } from './@types'
import { getSize } from './helpers'

export const ButtonWithType = ({ children, submit, ...props }: StyledButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const btnType = submit ? 'submit' : 'button'

  return (
    <button {...props} type={btnType} ref={buttonRef}>
      {children}
    </button>
  )
}

export const StyledButton = styled(ButtonWithType)`
  position: relative;

  margin: 0;
  padding: ${({ size }) => getSize(size).padding};

  height: fit-content;

  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  gap: 1.2rem;

  font-family: ${(props) => props.theme.font.family.default};
  font-weight: ${(props) => props.theme.font.weight.regular};

  font-size: ${({ size }) => getSize(size).fontSize};
  line-height: ${({ size }) => getSize(size).lineHeight};
  letter-spacing: ${({ size }) => getSize(size).letterSpacing};

  text-decoration: none;
  text-transform: ${({ isuppercase }) => (isuppercase === 'true' ? 'uppercase' : 'none')};

  border: 0.1rem solid;
  border-radius: 0.8rem;

  transition: all 0.26s ease;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  overflow: hidden;
  outline: none;
  z-index: ${(props) => props.theme.zIndex.minimal};

  &:disabled {
    opacity: ${(props) => props.theme.opacity.opacity05};
  }

  ${(props) =>
    props.type === 'default' &&
    css`
      color: ${(props) => props.theme.color.textPrimary};
      background-color: ${(props) => props.theme.color.buttonBg};
      border-color: ${(props) => props.theme.color.buttonBg};
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity02};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        box-shadow: 0 0 0 0.3rem ${(props) => transparentize(0.5, props.theme.color.buttonBg)};
      }
    `}

  ${(props) =>
    props.type === 'primary' &&
    css`
      color: ${(props) => props.theme.color.text};
      background-color: ${(props) => props.theme.color.buttonBgThird};
      border-color: ${(props) => props.theme.color.buttonBgThird};
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        box-shadow: 0 0 0 0.3rem ${(props) => transparentize(0.5, props.theme.color.buttonBgThird)};
      }
    `}

  ${(props) =>
    props.type === 'outline' &&
    css`
      color: ${(props) => props.theme.color.text};
      background-color: ${(props) => props.theme.color.transparent};
      border-color: ${(props) => props.theme.color.elementBorder};
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        box-shadow: 0 0 0 0.3rem ${(props) => transparentize(0.5, props.theme.color.elementBorder)};
      }
    `}

  ${(props) =>
    props.type === 'status' &&
    css`
      font-weight: ${(props) => props.theme.font.weight.regular};
      color: ${(props) => props.theme.color.text};
      background-color: ${(props) => props.theme.color.elementBg};
      border-radius: 5rem;
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        color: ${(props) => props.theme.color.textPrimary};
        background-color: ${(props) => props.theme.color.elementBgSecond};
      }
    `}

  ${(props) =>
    props.type === 'link' &&
    css`
      padding: 0;
      font-weight: ${(props) => props.theme.font.weight.regular};
      color: ${(props) => props.theme.color.textHintThird};
      background-color: ${(props) => props.theme.color.transparent};
      border: none;
      border-radius: 0;
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        color: ${(props) => darken(0.1, props.theme.color.textHint)};
      }
    `}

  ${(props) =>
    props.type === 'symbol' &&
    css`
      color: ${(props) => props.theme.color.text};
      background-color: ${(props) => props.theme.color.transparent};
      border: none;
      border-radius: 1.2rem;
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        background-color: ${(props) => transparentize(0.95, props.theme.color.elementBgSecond)};
      }
    `}

  ${(props) =>
    props.type === 'danger' &&
    css`
      color: ${(props) => props.theme.color.textPrimary};
      background-color: ${(props) => props.theme.color.danger};
      border-color: ${(props) => props.theme.color.danger};
      &:disabled {
        opacity: ${(props) => props.theme.opacity.opacity05};
      }
      &:hover:not([disabled]),
      &:focus:not([disabled]),
      &:active:not([disabled]) {
        box-shadow: 0 0 0 0.3rem ${(props) => transparentize(0.5, props.theme.color.danger)};
      }
    `}


  ${(props) =>
    props.fullsize === 'true' &&
    css`
      width: 100%;
    `}

  ${(props) =>
    props.isloading === 'true' &&
    css`
      position: relative;
      pointer-events: none;
      user-select: none;
      gap: 1.2rem;
      font-weight: ${(props) => props.theme.font.weight.medium};
      color: ${(props) => props.theme.color.text};
      background-color: ${(props) => transparentize(0.8, props.theme.color.default)};
      overflow: hidden;
    `}
`
